import { Lock, ShieldAlert } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';

export interface RequiredCapabilityBadgeProps {
  /** Capability slugs the plugin gates itself behind. */
  requiredCapabilities: readonly string[];
  /** Stable test id suffix (typically the plugin name). */
  testIdSuffix: string;
}

/**
 * Renders a compact "Requires capability X" badge for plugins that declare
 * `requiredCapabilities`. Visible in admin catalogs so operators can see at
 * a glance why a plugin is unavailable to a tenant that lacks the slug.
 *
 * Multiple capabilities collapse to "X +N more" with a tooltip listing the
 * full set so a single row never grows unbounded.
 */
export function RequiredCapabilityBadge({
  requiredCapabilities,
  testIdSuffix,
}: RequiredCapabilityBadgeProps) {
  if (requiredCapabilities.length === 0) return null;
  const [first, ...rest] = requiredCapabilities;
  const label = rest.length === 0 ? `Requires ${first}` : `Requires ${first} +${rest.length} more`;
  const badge = (
    <Badge
      variant="outline"
      className="gap-1 text-2xs font-normal"
      data-testid={`plugin-row-${testIdSuffix}-requires-capability`}
    >
      <Lock className="h-3 w-3" />
      {label}
    </Badge>
  );
  if (rest.length === 0) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>Requires: {requiredCapabilities.join(', ')}</TooltipContent>
    </Tooltip>
  );
}

export interface MissingCapabilityBadgeProps {
  /** Capability slugs the org is missing. */
  missingCapabilities: readonly string[];
  /** Stable test id suffix (typically the plugin name). */
  testIdSuffix: string;
}

/**
 * Renders a destructive "Missing capability X" badge for stranded plugin
 * installations — rows the server kept on `installed` because the org
 * once held the capability but has since lost it. Pairs with the
 * `unavailableReason: 'missing_capabilities'` field on `InstallationView`.
 *
 * Surfaces the *org-specific* lock reason; if the plugin is gated but the
 * org satisfies the gate, use {@link RequiredCapabilityBadge} instead.
 */
export function MissingCapabilityBadge({
  missingCapabilities,
  testIdSuffix,
}: MissingCapabilityBadgeProps) {
  if (missingCapabilities.length === 0) return null;
  const [first, ...rest] = missingCapabilities;
  const label =
    rest.length === 0
      ? `Missing capability: ${first}`
      : `Missing capability: ${first} +${rest.length}`;
  const badge = (
    <Badge
      variant="destructive"
      className="gap-1 text-2xs font-normal"
      data-testid={`plugin-row-${testIdSuffix}-missing-capability`}
    >
      <ShieldAlert className="h-3 w-3" />
      {label}
    </Badge>
  );
  if (rest.length === 0) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>Missing: {missingCapabilities.join(', ')}</TooltipContent>
    </Tooltip>
  );
}
